// What the Compare page can put side by side: two accounts, two weeks or months, two instruments,
// futures vs stocks, micros vs minis, sessions, weekdays, Playbook plays, tags, long vs short and
// hold times. Each choice is a group of trades with a name and a test. Also "Your best": every way
// of slicing your trades, with the best and worst group in each.
import { holdMinutes } from './insights';
import { netPnl, tradingDayKey } from './pnl';
import { CONTRACTS, isOptionSymbol, pointValue, rootSymbol } from './tools/contracts';
import { currentLocale, translateNow as tn } from './langState';

const keyDate = (key) => new Date(`${key}T12:00:00`);
const dayOf = (t) => tradingDayKey(t.date);
const nyMinutes = (t) => {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour12: false, hour: '2-digit', minute: '2-digit' }).formatToParts(new Date(t.date));
  const get = (k) => Number(parts.find((p) => p.type === k).value);
  return (get('hour') % 24) * 60 + get('minute');
};

export const COMPARE_TYPES = [
  ['accounts', 'Accounts'],
  ['periods', 'Time periods'],
  ['instruments', 'Instruments'],
  ['markets', 'Futures, stocks and options'],
  ['size', 'Micros vs minis'],
  ['sessions', 'Sessions'],
  ['weekdays', 'Days of the week'],
  ['plays', 'Playbook strategies'],
  ['tags', 'Tags'],
  ['sides', 'Long vs short'],
  ['hold', 'Hold time'],
  ['news', 'News days vs quiet days'],
  ['volatility', 'Calm vs volatile markets'],
];

// How volatile the market was that day, from the VIX closing level (see marketCalendar.js).
export const VOLATILITY = [
  ['calm', 'Calm market (VIX under 15)'],
  ['normal', 'Normal market (VIX 15–20)'],
  ['elevated', 'Volatile market (VIX 20–30)'],
  ['high', 'Very volatile market (VIX 30+)'],
];

export const PERIOD_UNITS = [['week', 'Weeks'], ['month', 'Months'], ['quarter', 'Quarters'], ['year', 'Years']];

// Sessions in New York time, the way futures traders talk about the day.
export const SESSIONS = [
  { id: 'open', label: 'New York open (9:30–11:00)', from: 570, to: 660 },
  { id: 'midday', label: 'Midday (11:00–2:00)', from: 660, to: 840 },
  { id: 'close', label: 'Afternoon and close (2:00–4:00)', from: 840, to: 960 },
  { id: 'after', label: 'After the close (4:00–6:00 p.m.)', from: 960, to: 1080 },
  { id: 'asia', label: 'Evening and Asia (6:00 p.m.–2:00 a.m.)', from: 1080, to: 120 },
  { id: 'london', label: 'London and pre-market (2:00–9:30 a.m.)', from: 120, to: 570 },
];
const inSession = (s, m) => (s.from < s.to ? m >= s.from && m < s.to : m >= s.from || m < s.to);

const HOLDS = [
  { id: 'scalp', label: 'Under 2 minutes', max: 2 },
  { id: 'short', label: '2–10 minutes', min: 2, max: 10 },
  { id: 'mid', label: '10–30 minutes', min: 10, max: 30 },
  { id: 'long', label: '30 minutes or more', min: 30 },
];

function marketOf(t) {
  if (isOptionSymbol(t.symbol)) return 'options';
  return pointValue(t.symbol) ? 'futures' : 'stocks';
}
function sizeOf(t) {
  const c = CONTRACTS.find((x) => x.symbol === rootSymbol(t.symbol));
  if (!c) return null;
  return c.full ? 'micro' : c.micro ? 'mini' : null;
}

// The period a trade belongs to, as a key that sorts in time order.
function periodKey(unit, t) {
  const day = dayOf(t);
  if (unit === 'year') return day.slice(0, 4);
  if (unit === 'month') return day.slice(0, 7);
  if (unit === 'quarter') return `${day.slice(0, 4)}-Q${Math.floor((Number(day.slice(5, 7)) - 1) / 3) + 1}`;
  const d = keyDate(day);
  d.setDate(d.getDate() - d.getDay());
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function periodLabel(unit, key) {
  const loc = currentLocale();
  if (unit === 'year') return key;
  if (unit === 'quarter') return `${key.slice(5)} ${key.slice(0, 4)}`;
  if (unit === 'month') return keyDate(`${key}-15`).toLocaleDateString(loc, { month: 'long', year: 'numeric' });
  return tn('Week of {date}', { date: keyDate(key).toLocaleDateString(loc, { month: 'short', day: 'numeric', year: 'numeric' }) });
}

// The choices for one kind of comparison: [{ id, label, test(trade) }], busiest first where it helps.
export function choicesFor(type, trades, { accounts = [], plays = [], library = [], unit = 'month', context = null } = {}) {
  const count = (test) => trades.filter(test).length;
  const withCount = (list) => list.map((c) => ({ ...c, n: count(c.test) })).filter((c) => c.n > 0);
  switch (type) {
    case 'accounts':
      return withCount(accounts.map((a) => ({ id: a._id, label: a.archived ? tn('{name} (archived)', { name: a.name }) : a.name, test: (t) => t.accountId === a._id })));
    case 'periods': {
      const keys = [...new Set(trades.map((t) => periodKey(unit, t)))].sort().reverse();
      return withCount(keys.map((k) => ({ id: k, label: periodLabel(unit, k), test: (t) => periodKey(unit, t) === k })));
    }
    case 'instruments': {
      const roots = [...new Set(trades.map((t) => rootSymbol(t.symbol)))];
      return withCount(roots.map((r) => ({ id: r, label: r, test: (t) => rootSymbol(t.symbol) === r }))).sort((a, b) => b.n - a.n);
    }
    case 'markets':
      return withCount([['futures', 'Futures'], ['stocks', 'Stocks'], ['options', 'Options']].map(([id, label]) => ({ id, label: tn(label), test: (t) => marketOf(t) === id })));
    case 'size':
      return withCount([['micro', 'Micros (MNQ, MES, MGC…)'], ['mini', 'Minis (NQ, ES, GC…)']].map(([id, label]) => ({ id, label: tn(label), test: (t) => sizeOf(t) === id })));
    case 'sessions':
      return withCount(SESSIONS.map((s) => ({ id: s.id, label: tn(s.label), test: (t) => !t.timeUnknown && inSession(s, nyMinutes(t)) })));
    case 'weekdays':
      return withCount([1, 2, 3, 4, 5, 0, 6].map((i) => ({
        id: String(i), label: new Date(2026, 0, 4 + i).toLocaleDateString(currentLocale(), { weekday: 'long' }),
        test: (t) => keyDate(dayOf(t)).getDay() === i,
      })));
    case 'plays':
      return withCount(plays.map((p) => ({ id: p.tag, label: p.name || p.tag, test: (t) => (t.tags || []).some((x) => x.toLowerCase() === p.tag.toLowerCase()) })));
    case 'tags': {
      const names = [...new Set(trades.flatMap((t) => t.tags || []))];
      return withCount(names.map((n) => ({ id: n, label: n, test: (t) => (t.tags || []).includes(n) }))).sort((a, b) => b.n - a.n);
    }
    case 'sides':
      return withCount([['Long', 'Long'], ['Short', 'Short']].map(([id, label]) => ({ id, label: tn(label), test: (t) => (t.side || 'Long') === id })));
    case 'hold':
      return withCount(HOLDS.map((h) => ({
        id: h.id, label: tn(h.label),
        test: (t) => { const m = holdMinutes(t); return m !== null && (h.min === undefined || m >= h.min) && (h.max === undefined || m < h.max); },
      })));
    case 'news': {
      if (!context) return [];
      const has = (t, label) => (context.news[dayOf(t)] || []).includes(label);
      const any = (t) => Boolean(context.news[dayOf(t)]);
      return withCount([
        { id: 'news', label: tn('Big news days (CPI, jobs report, Fed)'), test: any },
        { id: 'quiet', label: tn('Days without big news'), test: (t) => !any(t) },
        { id: 'cpi', label: tn('CPI days'), test: (t) => has(t, 'CPI') },
        { id: 'nfp', label: tn('Jobs report days'), test: (t) => has(t, 'NFP') },
        { id: 'fomc', label: tn('Fed decision days'), test: (t) => has(t, 'FOMC') },
      ]);
    }
    case 'newsSimple':
      return choicesFor('news', trades, { context }).filter((c) => c.id === 'news' || c.id === 'quiet');
    case 'volatility':
      if (!context) return [];
      return withCount(VOLATILITY.map(([id, label]) => ({ id, label: tn(label), test: (t) => context.volatility[dayOf(t)] === id })));
    default:
      return [];
  }
}

// Comparisons of two time periods line up by trading day (day 1, day 2…) instead of by date.
export function equityByTradingDay(tradesA, tradesB) {
  const daily = (trades) => {
    const m = new Map();
    for (const t of [...trades].sort((x, y) => new Date(x.date) - new Date(y.date))) m.set(dayOf(t), (m.get(dayOf(t)) || 0) + netPnl(t));
    return [...m.values()];
  };
  const a = daily(tradesA);
  const b = daily(tradesB);
  let ra = 0;
  let rb = 0;
  return Array.from({ length: Math.max(a.length, b.length) }, (_, i) => {
    ra += a[i] || 0;
    rb += b[i] || 0;
    return { key: String(i + 1), a: ra, b: rb, dayA: a[i] || 0, dayB: b[i] || 0, byIndex: true };
  });
}

// --- Your best ------------------------------------------------------------------

const MIN_GROUP = 8; // trades in a group before it can be called your best or worst

function groupStats(list) {
  const nets = list.map(netPnl);
  const net = nets.reduce((a, b) => a + b, 0);
  const wins = nets.filter((n) => n > 0);
  const losses = nets.filter((n) => n < 0);
  const grossWin = wins.reduce((a, b) => a + b, 0);
  const grossLoss = -losses.reduce((a, b) => a + b, 0);
  return {
    count: list.length, net, perTrade: list.length ? net / list.length : 0,
    winRate: list.length ? (wins.length / list.length) * 100 : 0,
    profitFactor: grossLoss > 0 ? grossWin / grossLoss : grossWin > 0 ? Infinity : null,
  };
}

// For each way of slicing the trades: every group with its numbers, and the best and worst
// group by net P&L per trade (only groups with enough trades count).
export function yourBest(trades, { plays = [], library = [], context = null } = {}) {
  const dims = [
    { id: 'year', title: 'Year', type: 'periods', unit: 'year' },
    { id: 'quarter', title: 'Quarter', type: 'periods', unit: 'quarter' },
    { id: 'month', title: 'Month', type: 'periods', unit: 'month' },
    { id: 'weekday', title: 'Day of the week', type: 'weekdays' },
    { id: 'session', title: 'Session', type: 'sessions' },
    { id: 'instrument', title: 'Instrument', type: 'instruments' },
    { id: 'market', title: 'Market', type: 'markets' },
    { id: 'size', title: 'Contract size', type: 'size' },
    { id: 'play', title: 'Playbook strategy', type: 'plays' },
    { id: 'tag', title: 'Tag', type: 'tags' },
    { id: 'side', title: 'Direction', type: 'sides' },
    { id: 'hold', title: 'Hold time', type: 'hold' },
    { id: 'volatility', title: 'Market volatility', type: 'volatility' },
    { id: 'news', title: 'News days', type: 'newsSimple' },
  ];
  return dims.map((d) => {
    const groups = choicesFor(d.type, trades, { plays, library, unit: d.unit, context })
      .map((c) => ({ id: c.id, label: c.label, ...groupStats(trades.filter(c.test)) }));
    const solid = groups.filter((g) => g.count >= MIN_GROUP);
    const ranked = [...solid].sort((a, b) => b.perTrade - a.perTrade);
    return {
      ...d, title: tn(d.title), groups,
      best: ranked.length >= 2 ? ranked[0] : null,
      worst: ranked.length >= 2 ? ranked[ranked.length - 1] : null,
    };
  }).filter((d) => d.groups.length >= 2);
}

export { MIN_GROUP };
